import { useId, useState } from 'react';
import { Card, Segmented, Tooltip } from 'antd';
import type { AnalyticsOnboardingResponse, OnboardingSnapshot, OnboardingSnapshotStep } from '@mygate/common';
import './OnboardingFunnel.css';

const number = new Intl.NumberFormat('de-DE');
const percentage = new Intl.NumberFormat('de-DE', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
function rate(reached: number, eligible: number) {
  return eligible > 0 ? percentage.format(reached / eligible) : '—';
}

const groups = [
  { id: 'profile', label: 'Profil & Identität' },
  { id: 'banking', label: 'Banking' },
  { id: 'investment', label: 'Investment & Depot' },
  { id: 'separate', label: 'Separat gemessen' },
] as const;

export function OnboardingFunnel({
  snapshot,
  summary,
}: {
  snapshot: OnboardingSnapshot;
  summary?: AnalyticsOnboardingResponse;
}) {
  const [view, setView] = useState<'overview' | 'table'>('overview');
  const titleId = useId();
  const overview = snapshot.steps.filter(step => step.overview);
  const hasCohort = snapshot.cohortSize > 0;

  const renderStep = (step: OnboardingSnapshotStep, index: number) => (
    <li className="onboarding-stage" key={step.id}>
      <span className="onboarding-stage-number" aria-hidden="true">
        {String(index + 1).padStart(2, '0')}
      </span>
      <span className="onboarding-stage-label">{step.label}</span>
      <span className="onboarding-stage-count">{number.format(step.reached)}</span>
      <span className="onboarding-bar-track" aria-hidden="true">
        <span style={{ width: `${hasCohort ? Math.min(100, (step.reached / snapshot.cohortSize) * 100) : 0}%` }} />
      </span>
      <span className="onboarding-stage-rate">{rate(step.reached, snapshot.cohortSize)}</span>
    </li>
  );

  const table = (
    <div className="onboarding-table-scroll" role="region" aria-label="Onboarding-Schritte im Detail" tabIndex={0}>
      <table className="onboarding-table">
        <caption>Erreichte Konten, Bezugsgruppe und Conversion je Schritt</caption>
        <thead>
          <tr>
            <th scope="col">Schritt</th>
            <th scope="col">Erreicht / Bezugsgruppe</th>
            <th scope="col">Conversion</th>
            <th scope="col">Anteil der Kohorte</th>
          </tr>
        </thead>
        {groups.map(group => (
          <tbody key={group.id}>
            <tr className="onboarding-table-group">
              <th colSpan={4} scope="rowgroup">
                {group.label}
              </th>
            </tr>
            {snapshot.steps
              .filter(step => step.group === group.id)
              .map(step => (
                <tr key={step.id}>
                  <th scope="row">
                    {step.label}
                    {step.note && <span className="onboarding-table-note">{step.note}</span>}
                    {!!step.outsidePrevious && (
                      <span className="onboarding-table-note">
                        {step.reached} insgesamt erreicht; {step.outsidePrevious} außerhalb der Bezugsgruppe.
                      </span>
                    )}
                  </th>
                  <td>
                    <strong>
                      {number.format(step.conversionReached ?? step.reached)} / {number.format(step.eligible)}
                    </strong>
                    <span className="onboarding-table-note">{step.referenceLabel}</span>
                  </td>
                  <td className="onboarding-table-percent">
                    {rate(step.conversionReached ?? step.reached, step.eligible)}
                  </td>
                  <td>{rate(step.reached, snapshot.cohortSize)}</td>
                </tr>
              ))}
          </tbody>
        ))}
      </table>
    </div>
  );

  return (
    <section className="onboarding-section" aria-labelledby={titleId}>
      <Card className="onboarding-card">
        <header className="onboarding-header">
          <h3 id={titleId}>Onboarding neuer Accounts</h3>
          <Segmented
            size="small"
            value={view}
            onChange={setView}
            aria-label="Darstellung der Onboarding-Statistik"
            options={[
              { label: 'Verlauf', value: 'overview' },
              { label: 'Tabelle', value: 'table' },
            ]}
          />
        </header>
        <div className="onboarding-body">
          <div className="onboarding-journey">
            <p className="onboarding-products">
              Verrechnungs- und Kryptokonto bilden die Basis. Das Wertpapierdepot für Aktien und ETFs wird separat
              freigeschaltet.
            </p>
            <div className="onboarding-chart-heading">
              <h4>Erreichte Schritte</h4>
              <span>Anteil an {number.format(snapshot.cohortSize)} neuen Accounts</span>
            </div>
            {!hasCohort ? (
              <p className="onboarding-empty">Keine Account-Neuanlagen in diesem Zeitraum.</p>
            ) : view === 'overview' ? (
              <ol className="onboarding-stages">{overview.map(renderStep)}</ol>
            ) : (
              <div className="onboarding-compact-table">
                <table>
                  <caption>Die wichtigsten Stationen</caption>
                  <thead>
                    <tr>
                      <th scope="col">Station</th>
                      <th scope="col">Accounts</th>
                      <th scope="col">Anteil</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.map(step => (
                      <tr key={step.id}>
                        <th scope="row">{step.label}</th>
                        <td>{number.format(step.reached)}</td>
                        <td>{rate(step.reached, snapshot.cohortSize)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <aside className="onboarding-observations" aria-label="Offene Schritte der Kohorte">
            <h4>Offene Schritte</h4>
            <div className="onboarding-observation-list">
              {snapshot.observations.map(item => (
                <Tooltip key={item.id} title={item.description} trigger={['hover', 'focus']}>
                  <div className="onboarding-observation" tabIndex={0}>
                    <span>{item.label}</span>
                    <strong>{number.format(item.count)}</strong>
                  </div>
                </Tooltip>
              ))}
            </div>
          </aside>
        </div>
        <details className="onboarding-details">
          <summary>Alle {snapshot.steps.length} Schritte</summary>
          {table}
        </details>
      </Card>
      <details className="analytics-source-details">
        <summary>Datengrundlage</summary>
        <dl className="analytics-definitions">
          <dt>Zeitraum</dt>
          <dd>
            Accounts, die im Zeitraum angelegt wurden, mit ihrem heute gespeicherten Onboarding-Status: kein Stand zum
            Ende des Zeitraums, kein Live-Abgleich mit Partnern. Importierte Accounts können ältere Zeiträume verzerren.
          </dd>
          <dt>Verrechnungs- &amp; Kryptokonto</dt>
          <dd>
            Bei Tradevest eingerichtet, unabhängig von Guthaben. Die Kennzahl oben zählt Abschlüsse im Zeitraum, der
            Verlauf den Fortschritt der neuen Accounts.
            {summary && ` Gesamt: ${summary.totals.tradevestCompleted} eingerichtet.`}
            {summary &&
              summary.period.tradevestCompletedWithoutDate > 0 &&
              ` ${summary.period.tradevestCompletedWithoutDate} Abschlüsse ohne Datum sind nicht in der Zeitraumzahl.`}
          </dd>
          <dt>Depot für Aktien &amp; ETFs</dt>
          <dd>
            Zusätzlich freigeschaltet, sagt nichts über Guthaben. Ältere Depots zählen mit, sofern sie nicht deaktiviert
            wurden; ihr Aktivierungsdatum fehlt oft.
            {summary &&
              ` Gesamt: ${summary.totals.securitiesDepotsExplicitlyActive} bestätigt aktiv, ${summary.totals.securitiesDepotsLegacyAvailable} ältere verfügbar.`}
          </dd>
          <dt>Conversion</dt>
          <dd>
            Verlauf: Anteil an allen neuen Accounts. Tabelle: Anteil an der Bezugsgruppe des Schritts. Offene Schritte
            sind keine bestätigten Abbrüche.
          </dd>
        </dl>
      </details>
    </section>
  );
}
